import React from 'react';
import styled from 'styled-components';

import { _ } from '@splunk/ui-utils/i18n';
import { variables, mixins } from '@splunk/themes';
import type { Mode } from '@splunk/dashboard-types';

import Tooltip from '@splunk/react-ui/Tooltip';

const Description = styled.div`
    ${mixins.reset('block')};
    min-height: 22px;
    max-width: 100%;
    margin: 0;
    padding: 2px 12px 0px 9px;
    box-sizing: inherit;
    line-height: 15px;
    font-size: 14px;
    font-weight: 300;
    color: ${variables.contentColorDefault};
    text-overflow: ellipsis;
`;

const DescriptionInput = styled.input`
    ${mixins.reset('block')};
    height: 22px;
    width: 100%;
    margin: 0;
    padding: 2px 12px 2px 8px; // Left & Bottom Padding is intentionally different than the above component for alignment.
    box-sizing: border-box;
    line-height: 15px;
    font-size: 14px;
    font-weight: 300;
    flex-grow: 0;
    color: ${variables.contentColorDefault};
    outline: none;
    background-color: transparent;
    border: transparent;

    &:hover,
    &:focus {
        background-color: ${variables.interactiveColorOverlayHover};
        border-color: ${variables.interactiveColorBorderHover};
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
    }
`;

interface DescriptionProps {
    handleBlur: React.FocusEventHandler;
    handleChange: React.FormEventHandler;
    handleKeyDown: React.KeyboardEventHandler;
    showInViewMode: boolean;
    mode: Mode;
    value?: string;
}

const DescriptionInputWrapper = React.forwardRef<
    HTMLInputElement,
    React.ComponentProps<typeof DescriptionInput> & { describedBy: string }
>(({ describedBy, ...props }, ref) => (
    <DescriptionInput ref={ref} aria-labelledby={describedBy} {...props} />
));

const DashboardDescription = React.forwardRef<
    HTMLDivElement | HTMLInputElement,
    DescriptionProps
>(
    (
        {
            handleBlur,
            handleChange,
            handleKeyDown,
            showInViewMode,
            mode,
            value,
        },
        ref
    ): JSX.Element | null => {
        if (mode === 'view' && !showInViewMode) {
            return null;
        }

        if (mode === 'edit') {
            return (
                <Tooltip content={_('Dashboard description')} inline={false}>
                    <DescriptionInputWrapper
                        ref={ref as React.MutableRefObject<HTMLInputElement>}
                        data-test="dashboard-description-input"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={_('Enter dashboard description.')}
                        value={value}
                        aria-label={_('Dashboard description')}
                    />
                </Tooltip>
            );
        }

        return (
            <Description
                ref={ref as React.MutableRefObject<HTMLDivElement>}
                data-test="dashboard-description"
            >
                {value}
            </Description>
        );
    }
);

export default React.memo(DashboardDescription);
